import type { MenuDataItem } from '@ant-design/pro-components';
import { ROUTES } from '../constants/routes';
import { withSidebarIcon } from './sidebarIcons';

type NavigationGroup = {
  key: string;
  path: string;
  locale: string;
  children: (string | NavigationGroup)[];
};
type NavigationModule = NavigationGroup & {
  defaultPath: string;
  externalUrl?: string;
};

const group = (
  key: string,
  path: string,
  locale: string,
  children: NavigationGroup['children'],
): NavigationGroup => ({ key: `nav.${key}`, path, locale, children });

// This is a menu hierarchy, not a router hierarchy. Business URLs stay unchanged.
export const OPERATOR_NAVIGATION: NavigationModule[] = [
  {
    ...group('home', '/home', 'menu.navigation.module.home', [
      ROUTES.park.profile,
      ROUTES.overview,
      // 待办中心拆两个菜单：审批是「做决定」，业务是「干活」，
      // 混在一个列表里会让人分不清当下该做什么。
      group('todos', ROUTES.todos, 'menu.todos.group', [
        ROUTES.todoApproval,
        ROUTES.todoBusiness,
      ]),
    ]),
    defaultPath: ROUTES.overview,
  },
  {
    ...group('assets', '/assets', 'menu.navigation.module.assets', [
      group(
        'spaces',
        ROUTES.park.spaceManagement,
        'menu.park.spaceManagement',
        [
          ROUTES.park.spacesOverview,
          ROUTES.park.spaces,
        ],
      ),
      ROUTES.park.otherAssets,
    ]),
    defaultPath: ROUTES.park.spacesOverview,
  },
  {
    ...group('attraction', '/attraction', 'menu.navigation.module.attraction', [
      group('leads', ROUTES.attraction.leads, 'menu.attraction.leads', [
        ROUTES.attraction.leadPool,
        ROUTES.attraction.leadMine,
        ROUTES.attraction.leadAll,
      ]),
      group(
        'customers',
        ROUTES.attraction.customers,
        'menu.attraction.customers',
        [
          ROUTES.attraction.customerPool,
          ROUTES.attraction.customerMine,
          ROUTES.attraction.customerAll,
        ],
      ),
      ROUTES.attraction.contacts,
      ROUTES.attraction.opportunities,
      ROUTES.attraction.orders,
      group(
        'attractionSetting',
        ROUTES.attraction.setting,
        'menu.attraction.setting',
        [ROUTES.attraction.claimConversion],
      ),
    ]),
    defaultPath: ROUTES.attraction.leadPool,
  },
  {
    ...group(
      'enterprises',
      '/enterprises',
      'menu.navigation.module.enterprises',
      [
        ROUTES.enterprises.list,
        group(
          'archives',
          ROUTES.enterprises.archives,
          'menu.enterprises.archives',
          [
            ROUTES.enterprises.qualifications,
            ROUTES.enterprises.intellectualProperties,
            ROUTES.enterprises.annualData,
          ],
        ),
        group('growth', '/growth', 'menu.navigation.incubationGrowth', [
          ROUTES.enterprises.incubationPlans,
          ROUTES.enterprises.mentors,
          ROUTES.growth.archives,
          ROUTES.growth.evaluations,
          ROUTES.growth.models,
          ROUTES.growth.recommendationRules,
        ]),
      ],
    ),
    defaultPath: ROUTES.enterprises.list,
  },
  {
    ...group('asset', '/asset', 'menu.navigation.module.asset', [
      ROUTES.asset.ledger,
      ROUTES.asset.suppliers,
      ROUTES.asset.reminders,
    ]),
    defaultPath: ROUTES.asset.ledger,
  },
  {
    ...group('contracts', '/contracts', 'menu.navigation.module.contracts', [
      ROUTES.contracts.management,
      group(
        'contractHandling',
        '/contract/handling',
        'menu.navigation.contractHandling',
        [ROUTES.contracts.all, ROUTES.contracts.mine, ROUTES.contracts.todo],
      ),
      group(
        'contractSettings',
        '/contract/setting',
        'menu.navigation.contractSettings',
        [
          ROUTES.contracts.types,
          ROUTES.contracts.templates,
          ROUTES.contracts.billingRules,
        ],
      ),
    ]),
    defaultPath: ROUTES.contracts.management,
  },
  {
    ...group('property', '/property', 'menu.navigation.module.property', [
      '/property/dashboard',
      group(
        'admission',
        '/enterprises/admission',
        'menu.navigation.admission',
        [
          ROUTES.enterprises.admissionApplications,
          '/enterprises/entries',
          ROUTES.enterprises.exits,
        ],
      ),
      ROUTES.park.workOrders,
      group('leases', ROUTES.park.leases, 'menu.park.leases', [
        '/park/leases/plans',
        '/park/leases/ledger',
        '/park/leases/renewals',
        '/park/leases/terminations',
      ]),
      ROUTES.park.parking,
      ROUTES.park.meetingRoom,
      ROUTES.park.accessControl,
      group('energy', ROUTES.park.energy, 'menu.property.energy', [
        ROUTES.park.energySpaces,
        ROUTES.park.energyMeters,
        ROUTES.park.energyLedger,
        ROUTES.park.energyAccounts,
        ROUTES.park.energyRates,
      ]),
      ROUTES.park.iotDevices,
      '/property/sla-rules',
    ]),
    defaultPath: '/property/dashboard',
  },
  {
    ...group('innovation', '/innovation', 'menu.navigation.module.innovation', [
      group('handling', ROUTES.services.handling, 'menu.services.handling', [
        ROUTES.services.matters,
        ROUTES.services.applications,
      ]),
      group(
        'marketplace',
        ROUTES.services.marketplace,
        'menu.services.marketplace',
        [
          ROUTES.services.demands,
          ROUTES.services.catalog,
          ROUTES.services.providers,
          ROUTES.services.orders,
          ROUTES.services.fulfillments,
          ROUTES.services.reviews,
        ],
      ),
      group('resources', '/resources', 'menu.navigation.resourceMatching', [
        ROUTES.resources.investment,
        ROUTES.resources.technologyResults,
        ROUTES.resources.researchInstitutions,
        ROUTES.resources.experts,
        ROUTES.resources.technologyManagers,
        ROUTES.resources.ecosystem,
      ]),
    ]),
    defaultPath: ROUTES.services.applications,
  },
  {
    ...group('operations', '/operations', 'menu.navigation.module.operations', [
      ROUTES.enterprises.visits,
      ROUTES.enterprises.requests,
      ROUTES.operations.activities,
      ROUTES.operations.activityOps,
      ROUTES.operations.policies,
      ROUTES.operations.announcements,
      ROUTES.operations.content,
      ROUTES.operations.partyBuilding,
      group('portal', '/portal', 'menu.portal', [
        ROUTES.portal.overview,
        ROUTES.portal.pages,
        ROUTES.portal.columns,
        ROUTES.portal.contents,
        ROUTES.portal.banners,
        ROUTES.portal.releases,
        ROUTES.portal.settings,
      ]),
    ]),
    defaultPath: ROUTES.operations.activities,
  },
  {
    ...group('hr', ROUTES.hr.root, 'menu.navigation.module.hr', [
      ROUTES.hr.root,
    ]),
    defaultPath: ROUTES.hr.root,
  },
  {
    ...group('finance', ROUTES.finance.root, 'menu.navigation.module.finance', [
      ROUTES.finance.overview,
      ROUTES.finance.receivables,
      ROUTES.finance.receipts,
      // 开票紧接收款：收完钱接着开票，是财务的一条连续动作。
      ROUTES.finance.invoices,
      ROUTES.finance.collectionLedger,
      ROUTES.finance.expenses,
    ]),
    defaultPath: ROUTES.finance.overview,
  },
  {
    ...group('evaluation', '/evaluation', 'menu.navigation.module.evaluation', [
      ROUTES.evaluation.overview,
      ROUTES.evaluation.indicatorSystems,
      ROUTES.evaluation.tasks,
      ROUTES.evaluation.reports,
    ]),
    defaultPath: ROUTES.evaluation.overview,
  },
  {
    ...group('reports', ROUTES.reports.root, 'menu.navigation.module.reports', [
      ROUTES.reports.overview,
      ROUTES.reports.investment,
      ROUTES.reports.finance,
      ROUTES.reports.property,
      ROUTES.reports.settings,
    ]),
    defaultPath: ROUTES.reports.overview,
  },
];

const flatten = (items: MenuDataItem[]): MenuDataItem[] =>
  items.flatMap((item) => [item, ...flatten(item.children || [])]);

const detach = (item: MenuDataItem): MenuDataItem => {
  const {
    children: _children,
    parentKeys: _parentKeys,
    pro_layout_parentKeys: _oldParents,
    ...rest
  } = item;
  return {
    ...rest,
    key: item.path,
    hideInMenu: !!item.hideInMenu,
    navigationModule: false,
    navigationGroup: false,
    navigationTarget: undefined,
  };
};

export const firstVisiblePath = (items: MenuDataItem[]): string | undefined => {
  for (const item of items) {
    if (item.hideInMenu || item.unaccessible || item.disabled || !item.name)
      continue;
    const path = item.children?.length
      ? firstVisiblePath(item.children)
      : item.path;
    if (path) return path;
  }
  return undefined;
};

export const navigationTarget = (item: MenuDataItem): string | undefined =>
  item.navigationTarget ||
  (item.children?.length ? firstVisiblePath(item.children) : item.path);

/** Reuse access-marked route entries; never manufacture access to a missing page. */
export function buildOperatorMenus(
  source: MenuDataItem[],
  translate: (id: string) => string,
  options: {
    tenantAdmin?: boolean;
    icons?: Record<string, MenuDataItem['icon']>;
  } = {},
): MenuDataItem[] {
  const indexed = new Map<string, MenuDataItem>();
  const collect = (items: MenuDataItem[], denied = false) => {
    items.forEach((item) => {
      const unaccessible = denied || !!item.unaccessible;
      if (item.path && !item.children?.length && !item.redirect) {
        indexed.set(item.path, { ...item, unaccessible });
      }
      collect(item.children || [], unaccessible);
    });
  };
  collect(source);

  const build = (definition: NavigationGroup): MenuDataItem | undefined => {
    const children = definition.children.flatMap((child): MenuDataItem[] => {
      if (typeof child !== 'string') {
        const result = build(child);
        return result ? [result] : [];
      }
      const route = indexed.get(child);
      if (
        !route?.name ||
        route.hideInMenu ||
        route.unaccessible ||
        route.disabled
      )
        return [];
      return [detach(route)];
    });
    if (!firstVisiblePath(children)) return undefined;
    return {
      key: definition.key,
      path: definition.path,
      locale: definition.locale,
      name: translate(definition.locale),
      children,
      hideInMenu: false,
      navigationGroup: true,
      navigationModule: false,
      navigationTarget: undefined,
    };
  };

  const menus: MenuDataItem[] = OPERATOR_NAVIGATION.flatMap((definition) => {
    const menu = build(definition);
    if (!menu) return [];
    const preferred = flatten(menu.children || []).find(
      (item) => item.path === definition.defaultPath,
    );
    return [
      {
        ...menu,
        children: menu.children?.map(withSidebarIcon),
        navigationModule: true,
        icon: options.icons?.[definition.key],
        navigationTarget:
          definition.externalUrl ||
          preferred?.path ||
          firstVisiblePath(menu.children || []),
      },
    ];
  });

  const settingsSource = source.find(
    (item) => item.path === ROUTES.settings.root,
  );
  if (settingsSource && !settingsSource.unaccessible) {
    const settingsChildren = (settingsSource.children || [])
      .filter(
        (item) =>
          item.name && !item.unaccessible && !item.disabled && !item.hideInMenu,
      )
      .map((item) => withSidebarIcon(detach(item)));
    if (settingsChildren.length)
      menus.push({
        ...settingsSource,
        key: 'nav.settings',
        locale: 'menu.navigation.module.settings',
        name: translate('menu.navigation.module.settings'),
        children: settingsChildren,
        hideInMenu: false,
        navigationModule: true,
        navigationGroup: true,
        navigationTarget: firstVisiblePath(settingsChildren),
      });
  }

  const leaves = flatten(menus).filter(
    (item) => !item.children?.length && !item.hideInMenu,
  );
  indexed.forEach((route) => {
    if (!route.hideInMenu || route.unaccessible || !route.path) return;
    const ownerPath =
      route.path === '/enterprises/:uuid'
        ? ROUTES.enterprises.list
        : route.path === '/attraction/campaigns'
          ? ROUTES.enterprises.investmentProjects
          : route.path === ROUTES.park.spaceIncubation ||
              route.path === ROUTES.park.spaceUsage
            ? ROUTES.park.spaces
            : undefined;
    const owner = leaves
      .filter((item) =>
        ownerPath
          ? item.path === ownerPath
          : route.path?.startsWith(`${item.path}/`),
      )
      .sort((a, b) => (b.path?.length || 0) - (a.path?.length || 0))[0];
    if (!owner?.key) return;
    const parent = flatten(menus).find((item) =>
      item.children?.includes(owner),
    );
    const detailLocale = route.path.endsWith('/claim')
      ? 'menu.navigation.claimReview'
      : route.path.endsWith('/new')
        ? 'menu.navigation.create'
        : route.path.endsWith('/edit')
          ? 'menu.navigation.edit'
          : route.path.endsWith('/reads')
            ? 'menu.navigation.reads'
            : 'menu.navigation.detail';
    parent?.children?.push({
      ...detach(route),
      parentKeys: [owner.key],
      name: route.name || translate(detailLocale),
      locale: route.locale || detailLocale,
      navigationOwner: owner.key,
    });
  });
  return menus;
}

const matchesPath = (pattern: string, pathname: string) => {
  const parts = pattern
    .split('/')
    .map((part) =>
      part.startsWith(':')
        ? '[^/]+'
        : part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
    );
  return new RegExp(`^${parts.join('/')}/?$`).test(pathname);
};

/** Breadcrumbs follow business ownership, including pages with historical prefixes. */
export function getNavigationTrail(
  pathname: string,
  menus: MenuDataItem[],
): MenuDataItem[] {
  const candidates: MenuDataItem[][] = [];
  const walk = (items: MenuDataItem[], parents: MenuDataItem[]) => {
    items.forEach((item) => {
      const trail = [...parents, item];
      if (item.path && matchesPath(item.path, pathname)) candidates.push(trail);
      walk(item.children || [], trail);
    });
  };
  walk(menus, []);
  const score = (trail: MenuDataItem[]) => {
    const path = trail[trail.length - 1].path || '';
    return path
      .split('/')
      .reduce((total, part) => total + (part.startsWith(':') ? 1 : 10), 0);
  };
  const result = candidates.sort((a, b) => score(b) - score(a))[0] || [];
  const detail = result[result.length - 1];
  if (detail?.navigationOwner) {
    const owner = flatten(menus).find(
      (item) => item.key === detail.navigationOwner,
    );
    if (owner) result.splice(result.length - 1, 0, owner);
  }
  return result;
}
